import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Users, LineChart, Coins, Shield, Link2, Crown,
  ChevronDown, Clock, CheckCircle, XCircle, AlertTriangle
} from "lucide-react";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useTheme, THEMES } from "../context/ThemeContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

var TIER_LABEL = { tier_3: "3 Strategy", tier_6: "6 Harmony", tier_9: "9 Power" };
var TIER_PRICE = { tier_6: 149, tier_9: 499, tier_3: 49 };
var ROLE_FILTERS = [
  { id: "all",      label: "All"      },
  { id: "client",   label: "Clients"  },
  { id: "investor", label: "Investors"},
];

export default function AdminDashboard() {
  var themeCtx  = useTheme();
  var theme     = themeCtx.theme;

  var [signups,    setSignups]    = useState([]);
  var [stats,      setStats]      = useState(null);
  var [loading,    setLoading]    = useState(true);
  var [error,      setError]      = useState("");
  var [query,      setQuery]      = useState("");
  var [roleFilter, setRoleFilter] = useState("all");
  var [activeTab,  setActiveTab]  = useState("pending"); // "pending" | "all"
  var [approving,  setApproving]  = useState({});

  var load = useCallback(async function () {
    setLoading(true);
    try {
      var signupParams = roleFilter === "all" ? {} : { role: roleFilter };
      var results = await Promise.all([
        api.get("/admin/signups", { params: signupParams }),
        api.get("/admin/stats"),
      ]);
      setSignups(results[0].data.items || []);
      setStats(results[1].data);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  useEffect(function () { load(); }, [load]);

  // Pending = users with role "user" or null (unverified/unpaid)
  var pendingUsers = useMemo(function () {
    return signups.filter(function (u) {
      return !u.role || u.role === "user";
    });
  }, [signups]);

  var filtered = useMemo(function () {
    var list = activeTab === "pending" ? pendingUsers : signups;
    var q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(function (u) {
      return (
        u.email.toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.referral || "").toLowerCase().includes(q) ||
        (u.mobile || "").toLowerCase().includes(q)
      );
    });
  }, [signups, pendingUsers, query, activeTab]);

  async function promote(userId, role) {
    setApproving(function (s) { var n = Object.assign({}, s); n[userId] = true; return n; });
    try {
      await api.post("/admin/users/" + userId + "/promote", { role: role });
      await load();
    } catch (e) {
      alert((e.response && e.response.data && e.response.data.detail) || e.message);
    }
    setApproving(function (s) { var n = Object.assign({}, s); n[userId] = false; return n; });
  }

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Header />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 lg:px-8">

        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-2">
          <Shield className="h-3 w-3 theme-text" /> Operator console
        </div>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight">
          Grid <span style={{ color: theme.color }}>Control</span>
        </h1>
        <p className="text-zinc-400 mt-2">Manage signups, verify payments, and approve Commander access.</p>

        {/* Stats */}
        <div className="mt-10 grid md:grid-cols-4 gap-4">
          <StatCard icon={<Users className="h-4 w-4" />}     label="Total signups"     value={stats ? stats.total    : "..."} />
          <StatCard icon={<Clock className="h-4 w-4" />}     label="Pending approval"  value={pendingUsers.length}
            highlight={pendingUsers.length > 0} color={pendingUsers.length > 0 ? "#f59e0b" : undefined} />
          <StatCard icon={<CheckCircle className="h-4 w-4" />} label="Approved clients" value={stats ? stats.clients  : "..."} />
          <StatCard icon={<Crown className="h-4 w-4" />}     label="Priority claimed"
            value={(stats && stats.priority) ? (stats.priority.claimed + " / " + stats.priority.total) : "..."} />
        </div>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <StatCard icon={<LineChart className="h-4 w-4" />} label="Harmony (tier 6)" value={(stats && stats.by_tier) ? stats.by_tier.tier_6 : "..."} />
          <StatCard icon={<LineChart className="h-4 w-4" />} label="Power (tier 9)"   value={(stats && stats.by_tier) ? stats.by_tier.tier_9 : "..."} />
          <StatCard icon={<Link2 className="h-4 w-4" />}     label="Investors"        value={stats ? stats.investors : "..."} />
        </div>

        {/* Tab switcher */}
        <div className="mt-12 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            {[
              { id: "pending", label: "Pending Approval", count: pendingUsers.length },
              { id: "all",     label: "All Signups",      count: signups.length      },
            ].map(function (tab) {
              return (
                <button key={tab.id} onClick={function () { setActiveTab(tab.id); }}
                  className="h-9 px-5 border text-xs font-bold tracking-widest uppercase transition-colors flex items-center gap-2"
                  style={{
                    borderColor: activeTab === tab.id ? theme.color : "rgba(255,255,255,0.12)",
                    color:       activeTab === tab.id ? theme.color : "#a1a1aa",
                    background:  activeTab === tab.id ? theme.color + "10" : "transparent",
                  }}>
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-black"
                      style={{ background: activeTab === tab.id ? theme.color : "rgba(255,255,255,0.1)", color: activeTab === tab.id ? "#000" : "#fff" }}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}

            {/* Role filter — only on "all" tab */}
            {activeTab === "all" && ROLE_FILTERS.map(function (r) {
              return (
                <button key={r.id} onClick={function () { setRoleFilter(r.id); }}
                  className="h-9 px-4 border text-xs font-bold tracking-widest uppercase transition-colors"
                  style={{
                    borderColor: roleFilter === r.id ? theme.color : "rgba(255,255,255,0.12)",
                    color:       roleFilter === r.id ? theme.color : "#a1a1aa",
                    background:  roleFilter === r.id ? theme.color + "10" : "transparent",
                  }}>
                  {r.label}
                </button>
              );
            })}
          </div>

          <input type="text" placeholder="Search name, email..." value={query}
            onChange={function (e) { setQuery(e.target.value); }}
            className="w-full sm:w-80 h-10 px-4 bg-black/60 border border-white/10 focus:border-[var(--theme-primary)] outline-none text-sm" />
        </div>

        {/* Pending notice banner */}
        {activeTab === "pending" && pendingUsers.length > 0 && (
          <div className="mt-5 flex items-start gap-3 px-5 py-4 border border-amber-500/30 bg-amber-500/5">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-amber-400">{pendingUsers.length} user{pendingUsers.length > 1 ? "s" : ""} awaiting approval</div>
              <p className="text-xs text-zinc-400 mt-0.5">
                Verify each payment in your <a href="https://nowpayments.io" target="_blank" rel="noreferrer"
                  className="text-amber-400 underline">NowPayments dashboard</a>, then click Approve below.
              </p>
            </div>
          </div>
        )}

        {activeTab === "pending" && !loading && pendingUsers.length === 0 && (
          <div className="mt-5 flex items-center gap-3 px-5 py-8 border border-white/10 bg-[#0A0A0A] justify-center">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <span className="text-zinc-400 text-sm">No pending approvals. All users are verified.</span>
          </div>
        )}

        {/* Table */}
        {(activeTab === "all" || (activeTab === "pending" && pendingUsers.length > 0)) && (
          <div className="mt-5 border border-white/10 bg-[#0A0A0A]">
            {error ? (
              <p className="p-6 text-sm text-red-400">{error}</p>
            ) : loading ? (
              <p className="p-6 text-sm text-zinc-400">Loading the grid...</p>
            ) : filtered.length === 0 ? (
              <p className="p-6 text-sm text-zinc-400">No signups match this filter.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500">Plan</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500">Amount Due</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500">Power Color</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500">Priority</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500">Joined</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-zinc-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(function (u) {
                    var themeMeta  = THEMES[u.theme] || THEMES.jupiter_gold;
                    var isPending  = !u.role || u.role === "user";
                    var planPrice  = u.tier ? TIER_PRICE[u.tier] : null;
                    return (
                      <TableRow key={u.id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="font-medium text-sm">{u.name || "—"}</TableCell>
                        <TableCell className="text-zinc-300 text-sm">{u.email}</TableCell>

                        {/* Status */}
                        <TableCell>
                          {isPending ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs border border-amber-500/40 text-amber-400">
                              <Clock className="h-3 w-3" /> Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs border border-green-500/40 text-green-400">
                              <CheckCircle className="h-3 w-3" /> {u.role}
                            </span>
                          )}
                        </TableCell>

                        {/* Plan */}
                        <TableCell className="text-zinc-300 text-sm">
                          {u.tier ? TIER_LABEL[u.tier] : "—"}
                        </TableCell>

                        {/* Amount due */}
                        <TableCell>
                          {planPrice ? (
                            <span className="font-mono text-sm font-bold" style={{ color: isPending ? "#f59e0b" : "#4ade80" }}>
                              ${planPrice}/mo
                            </span>
                          ) : (
                            <span className="text-zinc-500 text-xs">—</span>
                          )}
                        </TableCell>

                        {/* Theme */}
                        <TableCell>
                          <span className="inline-flex items-center gap-2 text-xs text-zinc-300">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: themeMeta.color }} />
                            {themeMeta.planet}
                          </span>
                        </TableCell>

                        {/* Priority */}
                        <TableCell>
                          {u.has_priority_access
                            ? <span className="theme-text font-semibold text-xs flex items-center gap-1"><Crown className="h-3 w-3" /> Premium</span>
                            : <span className="text-zinc-500 text-xs">—</span>}
                        </TableCell>

                        {/* Joined */}
                        <TableCell className="text-zinc-500 text-xs">
                          {new Date(u.created_at).toLocaleDateString()}
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="text-right">
                          {isPending ? (
                            // Pending user — show Approve / Reject
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={function () { promote(u.id, "client"); }}
                                disabled={approving[u.id]}
                                className="inline-flex items-center gap-1.5 h-8 px-4 text-xs font-bold tracking-widest uppercase border transition-all disabled:opacity-50"
                                style={{ borderColor: "#4ade8040", color: "#4ade80", background: "#4ade8008" }}>
                                {approving[u.id] ? "..." : "Approve"}
                              </button>
                              <button
                                onClick={function () { promote(u.id, "rejected"); }}
                                disabled={approving[u.id]}
                                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-50">
                                <XCircle className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : (
                            // Approved user — promote dropdown
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="inline-flex items-center gap-1 h-8 px-3 border border-white/10 hover:border-white/30 text-xs">
                                  Promote <ChevronDown className="h-3 w-3" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-[#0A0A0A] border-white/10 text-white">
                                <DropdownMenuItem onClick={function () { promote(u.id, "client"); }} disabled={u.role === "client"}>
                                  Set as Client
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={function () { promote(u.id, "investor"); }} disabled={u.role === "investor"}>
                                  Set as Investor
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard(props) {
  return (
    <div className="p-5 border border-white/10 bg-[#0A0A0A]"
      style={props.highlight ? { borderColor: "#f59e0b40", background: "#f59e0b05" } : {}}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-3">
        <span className="theme-text">{props.icon}</span> {props.label}
      </div>
      <div className="font-display font-black text-3xl"
        style={{ color: props.color || "inherit" }}>
        {props.value}
      </div>
      {props.sub && <div className="text-xs text-zinc-500 mt-1">{props.sub}</div>}
    </div>
  );
}
